import { LinkGroupType } from "components/Sidebar/Links"
import { BlockType, AllScience35Blocks } from "shared/types/science35Types"
import { faHome, faTasks, faUserGraduate, faBook, faChartLine, faCog, faFolder, faSchool, faNewspaper } from "@fortawesome/free-solid-svg-icons"
import { toProductString } from "./blockStringConverter"
import { HomeSchoolBlock } from "shared/types/homeschoolTypes"
import { SidebarLink } from "components/Sidebar/Links"

interface Props {
    hasStemSimsAccess: boolean
    hasCurriculumAccess: boolean
    hasScience21Access: boolean
    homeschoolBlocks: HomeSchoolBlock[]
    scienceBlocks: BlockType[]
}

export function generateSidebarContents({ hasStemSimsAccess, hasCurriculumAccess, hasScience21Access, homeschoolBlocks, scienceBlocks } : Props): LinkGroupType[] {
    const generalSidebarContents: LinkGroupType = {
        header: null,
        links: [
            {
                title: "Overview",
                path: "/dashboard/teacher/overview",
                icon: faHome,
                available: true,
            },
            {
                title: "Students",
                path: "/dashboard/teacher/students",
                icon: faUserGraduate,
                available: true,
            },
            {
                title: "Settings",
                path: "/dashboard/teacher/settings",
                icon: faCog,
                available: true,
            },
        ]
    }

    const hasScience35Access = AllScience35Blocks.some(block => scienceBlocks.includes(block))
    const hasScience68Access = scienceBlocks.includes("Science 68")
    const hasScienceBioAccess = scienceBlocks.includes("Science Bio")
    const hasScienceESSAccess = scienceBlocks.includes("Science ESS")

    if (hasStemSimsAccess) {
        generalSidebarContents.links.push({
            title: "Lesson Plans",
            path: "/dashboard/teacher/lesson-plans",
            icon: faFolder,
            available: true,
        })
    }
    generalSidebarContents.links.push("separator")

    const stemSidebarContents: LinkGroupType = {
        header: { title: "STEM Sims™", available: hasStemSimsAccess },
        links: [
            {
                title: "Assignments",
                path: "/dashboard/teacher/assignments",
                icon: faTasks,
                available: true,
            },
            {
                title: "Gradebook",
                path: "/dashboard/teacher/gradebook",
                icon: faBook,
                available: true,
            },
            {
                title: "Analytics",
                path: "/dashboard/teacher/analytics",
                icon: faChartLine,
                available: true,
            },
            "separator"
        ]
    }

    const escience21SidebarContents : LinkGroupType = {
        header: {
            title: "eScience21™",
            available: hasCurriculumAccess && hasScience21Access,
        },
        links: generateTextbookLinks({ type: "eScience21" })
    }

    const science35SidebarContents : LinkGroupType = {
        header: {
            title: "Sci35™",
            available: hasScience35Access,
        },
        links: generateScienceLinks({ blockType: "Science 35", hasAccess: hasScience35Access })
    }

    const science68SidebarContents : LinkGroupType = {
        header: {
            title: "Sci68™",
            available: hasScience68Access,
        },
        links: generateScienceLinks({ blockType: "Science 68", hasAccess: hasScience68Access })
    }

    const scienceBioSidebarContents : LinkGroupType = {
        header: {
            title: "Sci4Bio™",
            available: hasScienceBioAccess,
        },
        links: generateScienceLinks({ blockType: "Science Bio", hasAccess: hasScienceBioAccess })
    }

    const scienceESSSidebarContents : LinkGroupType = {
        header: {
            title: "Sci4ESS™",
            available: hasScienceESSAccess,
        },
        links: generateScienceLinks({ blockType: "Science ESS", hasAccess: hasScienceESSAccess })
    }

    const homeschool35SidebarContents : LinkGroupType = {
        header: {
            title: "Homeschool 35",
            available: hasCurriculumAccess && homeschoolBlocks.some(b => b === "Homeschool 35"),
        },
        links: generateTextbookLinks({ type: "homeschool35" })
    }

    const homeschool68SidebarContents : LinkGroupType = {
        header: {
            title: "Homeschool 68",
            available: hasCurriculumAccess && homeschoolBlocks.some(b => b === "Homeschool 68"),
        },
        links: generateTextbookLinks({ type: "homeschool68" })
    }

    const contents = [
        generalSidebarContents,
        escience21SidebarContents,
        stemSidebarContents,
        science35SidebarContents,
        science68SidebarContents,
        scienceBioSidebarContents,
        scienceESSSidebarContents,
        homeschool35SidebarContents,
        homeschool68SidebarContents,
    ].sort((a, b) => {
        const aAvailable = a.header === null ? true : a.header.available
        const bAvailable = b.header === null ? true : b.header.available
        return Number(bAvailable) - Number(aAvailable)
    })

    return contents

}

function generateTextbookLinks({ type } : { type: "eScience21" | "homeschool35" | "homeschool68" }): (SidebarLink | "separator")[] {
    return [
        {
            title: "Assignments",
            path: `/dashboard/teacher/textbook/${type}/assignments`,
            icon: faTasks,
            available: true,
        },
        {
            title: "Gradebook",
            path: `/dashboard/teacher/textbook/${type}/gradebook`,
            icon: faBook,
            available: true,
        },
        {
            title: "Documents",
            path: `/dashboard/teacher/textbook/${type}/documents`,
            icon: faNewspaper,
            available: true,
        },
        "separator"
    ]
}

function generateScienceLinks({ blockType, hasAccess } :  { blockType: BlockType, hasAccess: boolean }) {
    const blockString = toProductString(blockType)
    return [
        {
            title: "Assignments",
            path: `/dashboard/teacher/science/${blockString}/assignments`,
            icon: faTasks,
            available: hasAccess,
        },
        {
            title: "Student Mastery",
            path: `/dashboard/teacher/science/${blockString}/student-mastery`,
            icon: faUserGraduate,
            available: hasAccess,
        },
        {
            title: "Class Mastery",
            path: `/dashboard/teacher/science/${blockString}/class-mastery`,
            icon: faSchool,
            available: hasAccess,
        },
        {
            title: "Documents",
            path: `/dashboard/teacher/science/${blockString}/documents`,
            icon: faNewspaper,
            available: hasAccess,
        },
    ]
}
